<template>
  <div
    class="flex h-dvh min-h-screen w-[var(--layout-sidebar-width)] flex-none flex-col bg-gray-dark gap-4 pt-[140px] pb-8 xl:gap-5 2xl:pt-[168px]"
  >
    <NavButton to="/" icon="m-icon:home">Главная</NavButton>
    <NavButton v-if="canViewGoods" to="/search" icon="m-icon:goods">Товар</NavButton>
    <NavButton v-if="canViewOrders" to="/orders" icon="m-icon:order">Заказы</NavButton>
    <NavButton v-if="canViewFinance" to="/finance" icon="m-icon:finance">Финансы</NavButton>
    <NavButton v-if="canViewDelivery" to="/delivery" icon="m-icon:delivery">Отгрузка</NavButton>
    <!-- <NavButton to="/logist" icon="m-icon:logist">Логист</NavButton> -->

    <div class="mt-20 px-5">
      <div class="w-[174px]">
        <div class="flex h-[50px] w-[50px] items-center justify-center overflow-hidden rounded-full bg-white-card">
          <img
            v-if="managerPhotoUrl"
            :src="managerPhotoUrl"
            alt="Фото менеджера"
            class="h-full w-full object-cover"
          />
          <span v-else class="text-[44px] leading-none font-helvetica-700 text-gray-ink">
            {{ managerInitial }}
          </span>
        </div>

        <p class="mt-3 text-[14px] leading-[150%] font-helvetica-700 text-white-card">
          Ваш менеджер
        </p>
        <p class="mt-1 text-[14px] leading-[150%] font-helvetica-400 text-white-card whitespace-pre-line">
          {{ managerNameDisplay }}
        </p>

        <a
          v-if="managerPhoneDisplay !== '—'"
          :href="managerPhoneHref"
          class="mt-1 block cursor-pointer text-[14px] leading-[150%] font-helvetica-400 text-extra-text-color no-underline transition-colors hover:text-orange"
        >
          {{ managerPhoneDisplay }}
        </a>
        <p v-else class="mt-1 text-[14px] leading-[150%] font-helvetica-400 text-extra-text-color">
          {{ managerPhoneDisplay }}
        </p>

        <a
          v-if="managerEmailDisplay !== '—'"
          :href="managerEmailHref"
          class="mt-1 block cursor-pointer text-[14px] leading-[150%] font-helvetica-400 text-extra-text-color break-all no-underline transition-colors hover:text-orange"
        >
          {{ managerEmailDisplay }}
        </a>
        <p
          v-else
          class="mt-1 text-[14px] leading-[150%] font-helvetica-400 text-extra-text-color"
        >
          {{ managerEmailDisplay }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from "vue";
import { useLoginResponse } from "~/composables/useLoginResponse";

type LoginResponse = {
  Менеджер?: string;
  ТелефонМенеджера?: string;
  EMailМенеджера?: string;
  ФотоМенеджера?: string;
  АссистентМенеджера?: string;
  ТелефонАссистентаМенеджера?: string;
  EMailАссистентаМенеджера?: string;
  ФотоАссистентаМенеджера?: string;
};

const { loginResponse, hydrateLoginResponse, canViewGoods, canViewOrders, canViewFinance, canViewDelivery } =
  useLoginResponse();
const managerInfo = computed(() => loginResponse.value as LoginResponse | null);

const normalizeText = (value: unknown) => {
  if (typeof value !== "string") return "";
  return value.trim();
};

const formatManagerName = (name: string) => {
  if (!name) return "—";
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length <= 2) return name;
  return `${parts[0]} ${parts[1]}\n${parts.slice(2).join(" ")}`;
};

const managerNameRaw = computed(() => {
  const source = managerInfo.value;
  if (!source) return "";
  return normalizeText(source.Менеджер) || normalizeText(source.АссистентМенеджера);
});

const managerNameDisplay = computed(() => formatManagerName(managerNameRaw.value));

const managerPhoneDisplay = computed(() => {
  const source = managerInfo.value;
  if (!source) return "—";
  return (
    normalizeText(source.ТелефонМенеджера) ||
    normalizeText(source.ТелефонАссистентаМенеджера) ||
    "—"
  );
});

const managerEmailDisplay = computed(() => {
  const source = managerInfo.value;
  if (!source) return "—";
  return (
    normalizeText(source.EMailМенеджера) ||
    normalizeText(source.EMailАссистентаМенеджера) ||
    "—"
  );
});

const managerInitial = computed(() => {
  const normalized = managerNameRaw.value;
  return normalized ? normalized.charAt(0).toUpperCase() : "М";
});

const managerPhotoUrl = computed(() => {
  const source = managerInfo.value;
  if (!source) return "";
  return normalizeText(source.ФотоМенеджера) || normalizeText(source.ФотоАссистентаМенеджера);
});

const managerPhoneHref = computed(() => `tel:${managerPhoneDisplay.value.replace(/\s+/g, "")}`);
const managerEmailHref = computed(() => `mailto:${managerEmailDisplay.value}`);

onMounted(() => {
  hydrateLoginResponse();
});
</script>
